import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import EmbeddedLayout from '../../../components/admin/EmbeddedLayout';
import { useUser } from '../../../hooks/useUser';
import rfidTagsController, { IRfidTag } from '../../../Api/rfidTagsController';
import employeesController, { IEmployee } from '../../../Api/employeesController';
import { Navigate } from 'react-router-dom';

type FilterKey = 'all' | 'active' | 'inactive';
type AssignedKey = 'all' | 'assigned' | 'unassigned';

type TagDraft = {
  uid: string;
  name: string;
  isActive: boolean;
};

const emptyDraft = (): TagDraft => ({ uid: '', name: '', isActive: true });

const RfidTagsPage = ({ embedded = false }: { embedded?: boolean }) => {
  const user = useUser();
  const isAdmin = user.hasPermission('rfid.read');

  const [filter, setFilter] = useState<FilterKey>('all');
  const [assignedFilter, setAssignedFilter] = useState<AssignedKey>('all');

  const [rows, setRows] = useState<IRfidTag[]>([]);
  const [employees, setEmployees] = useState<IEmployee[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [scanUid, setScanUid] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TagDraft>(emptyDraft());

  const [openAssign, setOpenAssign] = useState(false);
  const [assigningTag, setAssigningTag] = useState<IRfidTag | null>(null);
  const [employeeIdToAssign, setEmployeeIdToAssign] = useState('');

  const buildParams = () => {
    const params: { isActive?: boolean; assigned?: boolean } = {};
    if (filter !== 'all') params.isActive = filter === 'active';
    if (assignedFilter !== 'all') params.assigned = assignedFilter === 'assigned';
    return Object.keys(params).length ? params : undefined;
  };

  const load = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoading(true);
    setError(null);
    setInfo(null);

    const [tagsRes, employeesRes] = await Promise.all([
      rfidTagsController.getAllRfidTags(buildParams()),
      employeesController.getAllEmployees(),
    ]);

    if (!tagsRes.ok) {
      setRows([]);
      setError(tagsRes.message || 'Nie udało się pobrać tagów RFID');
      setIsLoading(false);
      return;
    }

    setRows(tagsRes.tags || []);

    if (employeesRes.ok) {
      setEmployees(employeesRes.employees || []);
    } else {
      setEmployees([]);
    }

    setIsLoading(false);
  }, [assignedFilter, filter, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setMode('create');
    setEditingId(null);
    setDraft(emptyDraft());
    setOpenDialog(true);
  };

  const openEdit = (tag: IRfidTag) => {
    setMode('edit');
    setEditingId(tag._id);
    setDraft({
      uid: tag.uid || '',
      name: tag.name || '',
      isActive: typeof tag.isActive === 'boolean' ? tag.isActive : true,
    });
    setOpenDialog(true);
  };

  const save = async () => {
    const uid = draft.uid.trim().toUpperCase();
    if (!uid) {
      setError('Podaj UID');
      return;
    }

    const name = draft.name.trim();

    setIsLoading(true);
    setError(null);
    setInfo(null);

    if (mode === 'create') {
      const res = await rfidTagsController.createRfidTag({ uid, name: name || undefined, isActive: draft.isActive });
      if (!res.ok) {
        setError(res.message || 'Nie udało się dodać taga RFID');
        setIsLoading(false);
        return;
      }
      setOpenDialog(false);
      await load();
      return;
    }

    if (!editingId) {
      setError('Brak ID taga do edycji');
      setIsLoading(false);
      return;
    }

    const res = await rfidTagsController.updateRfidTag(editingId, { uid, name, isActive: draft.isActive });
    if (!res.ok) {
      setError(res.message || 'Nie udało się zaktualizować taga RFID');
      setIsLoading(false);
      return;
    }

    setOpenDialog(false);
    await load();
  };

  const openAssignDialog = (tag: IRfidTag) => {
    setAssigningTag(tag);
    setEmployeeIdToAssign(tag.employee?._id || '');
    setOpenAssign(true);
  };

  const doAssign = async () => {
    if (!assigningTag) return;
    if (!employeeIdToAssign) {
      setError('Wybierz pracownika');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInfo(null);

    const res = await rfidTagsController.assignEmployeeToTag(assigningTag._id, employeeIdToAssign);
    if (!res.ok) {
      setError(res.message || 'Nie udało się przypisać pracownika');
      setIsLoading(false);
      return;
    }

    setOpenAssign(false);
    await load();
  };

  const onScanKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (e.key !== 'Enter') return;

    const uid = scanUid.trim().toUpperCase();
    if (!uid) return;

    setIsLoading(true);
    setError(null);
    setInfo(null);

    const res = await rfidTagsController.createRfidTag({ uid });

    if (!res.ok) {
      setIsLoading(false);
      setError(res.message || 'Nie udało się dodać taga RFID');
      return;
    }

    setScanUid('');
    setInfo('Dodano nowy tag RFID (ze skanu). Możesz nadać mu nazwę w edycji.');
    await load();
  };

  const columns = useMemo<GridColDef<IRfidTag>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Nazwa',
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => row.name || '—',
        renderCell: (params) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>{params.row.name || '—'}</Box>
        ),
      },
      {
        field: 'uid',
        headerName: 'UID',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>{params.row.uid}</Box>
        ),
      },
      {
        field: 'isActive',
        headerName: 'Aktywny',
        width: 120,
        valueGetter: (_value, row) => (row.isActive ? 'Tak' : 'Nie'),
        renderCell: (params) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            {params.row.isActive ? 'Tak' : 'Nie'}
          </Box>
        ),
      },
      {
        field: 'employee',
        headerName: 'Pracownik',
        flex: 1,
        minWidth: 220,
        valueGetter: (_value, row) => {
          if (!row.employee) return '—';
          return `${row.employee.lastName} ${row.employee.firstName}`;
        },
        renderCell: (params) => {
          const employee = params.row.employee;
          const label = employee ? `${employee.lastName} ${employee.firstName}` : '—';
          return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>{label}</Box>;
        },
      },
      {
        field: 'actions',
        headerName: 'Akcje',
        flex: 1,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box sx={{ width: '100%', py: 1, display: 'flex', alignItems: 'center' }}>
              <Stack direction="row" gap={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
                  Edytuj
                </Button>
                <Button size="small" variant="outlined" onClick={() => openAssignDialog(row)}>
                  Przypisz
                </Button>
              </Stack>
            </Box>
          );
        },
      },
    ],
    [employees]
  );

  const authChecked = user.authReady;

  if (!authChecked) {
    return (
      <EmbeddedLayout embedded={embedded} title="RFID">
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <CircularProgress size={22} />
            <Typography>Sprawdzam autoryzację…</Typography>
          </Stack>
        </Paper>
      </EmbeddedLayout>
    );
  }

  if (!isAdmin) return <Navigate to="/logowanie" replace />;

  return (
    <EmbeddedLayout embedded={embedded} title="RFID">
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>
              RFID
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Zarządzanie kartami/tagami RFID (lista, dodawanie, edycja, przypisywanie)
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button variant="contained" onClick={openCreate}>
              Dodaj tag
            </Button>
            <Button variant="outlined" onClick={load} startIcon={isLoading ? <CircularProgress size={18} /> : undefined}>
              Odśwież
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack gap={1.5} sx={{ mb: 2 }}>
          <TextField
            label="Skanuj kartę (UID) i wciśnij Enter"
            value={scanUid}
            onChange={(e) => setScanUid(e.target.value)}
            onKeyDown={onScanKeyDown}
            fullWidth
          />

          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip label="Wszyscy" clickable variant={filter === 'all' ? 'filled' : 'outlined'} onClick={() => setFilter('all')} />
            <Chip label="Aktywne" clickable variant={filter === 'active' ? 'filled' : 'outlined'} onClick={() => setFilter('active')} />
            <Chip label="Nieaktywne" clickable variant={filter === 'inactive' ? 'filled' : 'outlined'} onClick={() => setFilter('inactive')} />

            <Box sx={{ width: 12 }} />

            <Chip
              label="Wszystkie (przypisanie)"
              clickable
              variant={assignedFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setAssignedFilter('all')}
            />
            <Chip
              label="Przypisane"
              clickable
              variant={assignedFilter === 'assigned' ? 'filled' : 'outlined'}
              onClick={() => setAssignedFilter('assigned')}
            />
            <Chip
              label="Nieprzypisane"
              clickable
              variant={assignedFilter === 'unassigned' ? 'filled' : 'outlined'}
              onClick={() => setAssignedFilter('unassigned')}
            />
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {info ? <Alert severity="success">{info}</Alert> : null}
        </Stack>

        <Box sx={{ height: 560, width: '100%', minWidth: 0 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r._id}
            loading={isLoading}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': {
                maxHeight: 'none !important',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{mode === 'create' ? 'Dodaj tag RFID' : 'Edytuj tag RFID'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="UID"
              value={draft.uid}
              onChange={(e) => setDraft((d) => ({ ...d, uid: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Nazwa (opcjonalnie)"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography fontWeight={800}>Aktywny</Typography>
                <Typography variant="caption" color="text.secondary">
                  Wyłącz, jeśli karta nie ma być używana.
                </Typography>
              </Box>
              <Switch checked={draft.isActive} onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Anuluj
          </Button>
          <Button onClick={save} variant="contained" disabled={isLoading}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAssign} onClose={() => setOpenAssign(false)} fullWidth maxWidth="sm">
        <DialogTitle>Przypisz pracownika do taga</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Pracownik"
              value={employeeIdToAssign}
              onChange={(e) => setEmployeeIdToAssign(e.target.value)}
              fullWidth
            >
              <MenuItem value="">— wybierz —</MenuItem>
              {employees
                .slice()
                .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`))
                .map((e) => (
                  <MenuItem key={e._id} value={e._id}>
                    {e.lastName} {e.firstName} {e.isActive ? '' : '(nieaktywny)'}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssign(false)} variant="outlined">
            Anuluj
          </Button>
          <Button onClick={doAssign} variant="contained" disabled={isLoading}>
            Przypisz
          </Button>
        </DialogActions>
      </Dialog>
    </EmbeddedLayout>
  );
};

export default RfidTagsPage;
